import { useState } from 'react';
import { AlertIntegrationType } from '@elementary/common-ui/services/gql/types';
import {
  ConnectedWorkspace,
  FetchSlackApi,
  IntegrationType,
  MessagingIntegration,
  SetSlackIntegrationResponse,
  SlackForm,
} from 'shared/types';
import { IntegrationError } from 'settings/types';
import { MESSAGING_INTEGRATION_TYPES } from '../IntegrationAccordion/constants';
import IntegrationSelection from './IntegrationSelection';
import SlackDialog from './SlackDialog';
import TeamsDialog from './TeamsDialog';
import TeamsBotDialog, { TeamsBotDialogStep } from './TeamsBotDialog';

type Props = {
  open: boolean;
  onClose: () => void;
  slackData?: FetchSlackApi;
  onSlackSubmit: (values: SlackForm) => void;
  onSlackIntegrationSubmit: () => void;
  slackIntegrationResponse?: SetSlackIntegrationResponse;
  error?: IntegrationError;
  slackIntegrationError?: IntegrationError;
  loading?: boolean;
  envId: string;
  slackInstalled?: boolean;
  isTeamsBotConnected?: boolean;
  isTeamsBotLoading?: boolean;
  teamsBotManifestUrl?: string;
  teamsBotAppId?: string;
  teamsBotConnectedWorkspaces?: ConnectedWorkspace[];
  forceTeamsBot?: boolean;
  teamsBotInitialStep?: TeamsBotDialogStep;
  teamsBotConnectError?: string | null;
  isTeamsBotLinking?: boolean;
  edit?: boolean;
  editingType?: AlertIntegrationType | null;
  connectedTypes?: AlertIntegrationType[];
  onDisconnect?: (integrationType: IntegrationType) => void;
};

const MessagingDialog = ({
  open,
  onClose,
  slackData,
  onSlackSubmit,
  onSlackIntegrationSubmit,
  slackIntegrationResponse,
  error,
  slackIntegrationError,
  loading,
  envId,
  slackInstalled,
  isTeamsBotConnected,
  isTeamsBotLoading,
  teamsBotManifestUrl,
  teamsBotAppId,
  teamsBotConnectedWorkspaces,
  forceTeamsBot,
  teamsBotInitialStep,
  teamsBotConnectError,
  isTeamsBotLinking,
  edit,
  editingType = null,
  connectedTypes = [],
  onDisconnect,
}: Props) => {
  const [selectedIntegration, setSelectedIntegration] =
    useState<MessagingIntegration | null>(null);

  const handleClose = () => {
    setSelectedIntegration(null);
    onClose?.();
  };

  if (!open) return null;

  // The deep-link connect flow (from the Teams welcome card) forces the Teams
  // App dialog open regardless of selection/edit state.
  if (
    forceTeamsBot ||
    selectedIntegration === 'TeamsBot' ||
    editingType === 'MICROSOFT_TEAMS_BOT'
  ) {
    return (
      <TeamsBotDialog
        open={open}
        onClose={handleClose}
        isConnected={Boolean(isTeamsBotConnected)}
        isLoading={isTeamsBotLoading}
        manifestUrl={teamsBotManifestUrl}
        appId={teamsBotAppId}
        envId={envId}
        connectedWorkspaces={teamsBotConnectedWorkspaces}
        initialStep={teamsBotInitialStep}
        connectError={teamsBotConnectError}
        isLinking={isTeamsBotLinking}
        onDisconnect={
          onDisconnect ? () => onDisconnect('microsoft_teams_bot') : undefined
        }
      />
    );
  }

  if (editingType) {
    if (editingType === 'SLACK') {
      return (
        <SlackDialog
          open={open}
          onClose={handleClose}
          data={slackData}
          onSubmit={onSlackSubmit}
          onSlackIntegrationSubmit={onSlackIntegrationSubmit}
          slackIntegrationResponse={slackIntegrationResponse}
          slackIntegrationError={slackIntegrationError}
          error={error}
          loading={loading}
          envId={envId}
          appInstalled={slackInstalled}
          edit={edit}
          onDisconnect={
            edit && onDisconnect ? () => onDisconnect('slack') : undefined
          }
        />
      );
    }
    if (editingType === 'MICROSOFT_TEAMS') {
      return (
        <TeamsDialog
          open={open}
          onClose={handleClose}
          loading={loading}
          envId={envId}
          onDisconnect={
            edit && onDisconnect
              ? () => onDisconnect('microsoft_teams')
              : undefined
          }
        />
      );
    }
    return null;
  }

  if (selectedIntegration === 'Slack') {
    return (
      <SlackDialog
        open={open}
        onClose={handleClose}
        data={slackData}
        onSubmit={onSlackSubmit}
        onSlackIntegrationSubmit={onSlackIntegrationSubmit}
        slackIntegrationResponse={slackIntegrationResponse}
        slackIntegrationError={slackIntegrationError}
        error={error}
        loading={loading}
        envId={envId}
        appInstalled={slackInstalled}
        edit={false}
      />
    );
  }

  if (selectedIntegration === 'Teams') {
    return (
      <TeamsDialog
        open={open}
        onClose={handleClose}
        loading={loading}
        envId={envId}
      />
    );
  }

  const unconnectedTypes = MESSAGING_INTEGRATION_TYPES.filter(
    (type) => !connectedTypes.includes(type),
  );

  if (unconnectedTypes.length === 1) {
    const type = unconnectedTypes[0];
    if (type === 'SLACK') {
      return (
        <SlackDialog
          open={open}
          onClose={handleClose}
          data={slackData}
          onSubmit={onSlackSubmit}
          onSlackIntegrationSubmit={onSlackIntegrationSubmit}
          slackIntegrationResponse={slackIntegrationResponse}
          slackIntegrationError={slackIntegrationError}
          error={error}
          loading={loading}
          envId={envId}
          appInstalled={slackInstalled}
          edit={false}
        />
      );
    }
    if (type === 'MICROSOFT_TEAMS') {
      return (
        <TeamsDialog
          open={open}
          onClose={handleClose}
          loading={loading}
          envId={envId}
        />
      );
    }
    // MICROSOFT_TEAMS_BOT falls through to IntegrationSelection; its alert
    // integration is created on connect (via /link), not on selection.
  }

  return (
    <IntegrationSelection
      open={!selectedIntegration}
      onClose={handleClose}
      onSelected={(value: MessagingIntegration) => {
        setSelectedIntegration(value);
      }}
      connectedTypes={connectedTypes}
    />
  );
};

export default MessagingDialog;
